import { ApiClient, IColorUser, IErrorPayload } from '@color/lib';
import {
  OnQueryErrorCallback,
  OnQuerySuccessCallback,
  useOnQueryError,
  useOnQuerySuccess,
} from '@color/lib/QueryUtils';
import { UseMutationOptions, UseQueryOptions, useMutation, useQuery } from '@tanstack/react-query';

import {
  IConversationWithMessageIds,
  IZendeskConversation,
  ZendeskUserJWTResponse,
} from '../types';
import { convertZendeskConversationToInternalRepresentation } from '../utils';

import {
  smoochGetConversationById,
  smoochGetConversations,
  smoochInitQuery,
  smoochLoginQuery,
  smoochSendMessageInConversation,
} from './smooch_api_utils';
import {
  IConversationResponse,
  IConversationUnreadStatusResponse,
  ICreateConversationPayload,
  ICreatePatientRequestedProviderMessagePayload,
  ICreatePatientRequestedProviderMessageResponse,
  IIsPatientOnboardedForMessagingResponse,
  IUnreadMessageCountResponse,
  IUpdateConversationLastOpenedAtPayload,
  IUpdateMessagesPayload,
} from './types';

const FIFTEEN_MINUTES_IN_MILLISECONDS = 15 * 60 * 1000;
export const useZendeskUserJWT = () => {
  return useQuery<ZendeskUserJWTResponse, IErrorPayload>({
    queryKey: ['/api/v1/patient_messaging/get_zendesk_user_jwt'],
    refetchInterval: FIFTEEN_MINUTES_IN_MILLISECONDS,
  });
};

export const IS_PATIENT_ONBOARDED_FOR_MESSAGING_ENDPOINT =
  '/api/v1/patient_messaging/is_patient_onboarded_for_messaging';
export const useIsPatientOnboardedForMessaging = (patient_id: string | undefined) => {
  return useQuery<IIsPatientOnboardedForMessagingResponse, IErrorPayload>({
    queryKey: [`${IS_PATIENT_ONBOARDED_FOR_MESSAGING_ENDPOINT}?patient_id=${patient_id}`],
    enabled: patient_id !== undefined,
  });
};

export const useConversationUnreadStatusesQuery = () => {
  return useQuery<IConversationUnreadStatusResponse, IErrorPayload>({
    queryKey: ['/api/v1/patient_messaging/get_unread_conversation_status_for_all_conversations'],
    refetchOnMount: 'always',
  });
};

export const useSmoochGetConversationsQuery = () => {
  return useQuery<IZendeskConversation[], IErrorPayload>({
    queryKey: ['conversations'],
    queryFn: smoochGetConversations,
    refetchOnMount: 'always',
  });
};

export const useBulkUpdateMessagesToReadMutation = () => {
  return useMutation<undefined, IErrorPayload, IUpdateMessagesPayload>({
    mutationFn: async (payload: IUpdateMessagesPayload) => {
      return ApiClient.put('/api/v1/patient_messaging/update_all_unread_messages_to_read', {
        json: payload,
      });
    },
  });
};

export const useUpdateConversationLastOpenedAtByPatientMutation = () => {
  return useMutation<undefined, IErrorPayload, IUpdateConversationLastOpenedAtPayload>({
    mutationFn: async (payload: IUpdateConversationLastOpenedAtPayload) => {
      return ApiClient.put(
        `/api/v1/patient_messaging/update_conversation_last_opened_at_by_patient/${payload.conversationExternalId}`
      );
    },
  });
};

export const useUpdateMessageToReadMutation = () => {
  return useMutation<undefined, IErrorPayload, IUpdateMessagesPayload>({
    mutationFn: async (payload: IUpdateMessagesPayload) => {
      return ApiClient.put('/api/v1/patient_messaging/update_message_to_read', { json: payload });
    },
  });
};

export const useSmoochInitQuery = () => {
  return useQuery<boolean, IErrorPayload>({
    queryKey: ['init'],
    queryFn: async () => {
      await smoochInitQuery();
      return true;
    },
  });
};

export const useSmoochLoginQuery = (
  isInitLoading: boolean,
  currentUser: IColorUser | undefined,
  jwtToken: string | undefined,
  setIsLoading: (isLoading: boolean) => void
) => {
  return useQuery<boolean, IErrorPayload>({
    queryKey: ['initAndLogin', jwtToken],
    queryFn: async () => {
      await smoochLoginQuery(currentUser?.patient_id ?? '', jwtToken ?? '', setIsLoading);
      return true;
    },
    enabled: jwtToken !== undefined && currentUser !== undefined && !isInitLoading,
  });
};

export const useSmoochGetConversationByIdQuery = (conversationId: string) => {
  return useQuery<IZendeskConversation, IErrorPayload>({
    queryKey: ['conversation', conversationId],
    queryFn: () => smoochGetConversationById(conversationId),
    // ConversationDetailView redirects from useOnQueryError, which also fires on a cached error at
    // mount. Dropping the query once unobserved keeps reopening a conversation fetch-driven.
    gcTime: 0,
  });
};

const fetchMessagesAndProcessConversations = async (
  fetchedConversations: IZendeskConversation[],
  conversationStatuses: Record<string, boolean>
): Promise<IConversationWithMessageIds[]> => {
  return fetchedConversations.reduce(
    async (
      conversationList: Promise<IConversationWithMessageIds[]>,
      conversation: IZendeskConversation
    ) => {
      const awaitedConversationList = await conversationList;
      const conversationDetailResponse = await smoochGetConversationById(conversation.id);
      if (conversationDetailResponse.messages.length > 0) {
        // The messages property may only have the most recent message but we need all messages
        // to accurately compute the participant list.
        // TODO (TELE-1833): This N+1 query might slow down the inbox view for a patient. We should start to
        // populate the participant list in the metadata for the conversation and pull it from there
        // instead.
        awaitedConversationList.push({
          ...convertZendeskConversationToInternalRepresentation({
            ...conversation,
            messages: conversationDetailResponse.messages,
          }),
          isUnreadByPatient: conversationStatuses.hasOwnProperty(conversation.id)
            ? conversationStatuses[conversation.id]
            : true,
        });
      }
      return awaitedConversationList;
    },
    Promise.resolve([])
  );
};

interface IFetchAndProcessConversationsQueryOptions extends Omit<
  UseQueryOptions<IConversationWithMessageIds[], IErrorPayload>,
  'queryKey'
> {
  onSuccess?: OnQuerySuccessCallback<IConversationWithMessageIds[]>;
  onError?: OnQueryErrorCallback<IErrorPayload>;
}

export const useFetchAndProcessConversations = (
  fetchedConversations: IZendeskConversation[],
  conversationUnreadStatuses: Record<string, boolean>,
  areConversationsLoading: boolean,
  areConversationStatusesLoading: boolean,
  queryOptions: IFetchAndProcessConversationsQueryOptions
) => {
  const { onSuccess, onError, ...restQueryOptions } = queryOptions;
  const query = useQuery<IConversationWithMessageIds[], IErrorPayload>({
    queryKey: ['processedConversations', fetchedConversations],
    queryFn: () =>
      fetchMessagesAndProcessConversations(fetchedConversations, conversationUnreadStatuses),
    ...restQueryOptions,
    enabled: !areConversationsLoading && !areConversationStatusesLoading,
    refetchOnMount: 'always',
  });
  useOnQuerySuccess(query, onSuccess);
  useOnQueryError(query, onError);
  return query;
};

const useCreatePatientInitiatedConversation = (
  options: UseMutationOptions<IConversationResponse, IErrorPayload, ICreateConversationPayload>
) => {
  return useMutation<IConversationResponse, IErrorPayload, ICreateConversationPayload>({
    mutationFn: async (payload: ICreateConversationPayload) => {
      return ApiClient.post('/api/v1/patient_messaging/patient_initiated_conversation', {
        json: { title: payload.subject, tags: payload.tags },
      });
    },
    ...options,
  });
};

export const useCreateConversationMutation = (
  onSuccess: (conversationId: string) => void,
  onError: () => void
) => {
  const onConversationCreationSuccess = (
    conversationResponse: IConversationResponse,
    conversationPayload: ICreateConversationPayload
  ) => {
    smoochSendMessageInConversation(
      conversationPayload.message,
      conversationResponse.conversation_external_id
    ).then(() => onSuccess(conversationResponse.conversation_external_id));
  };
  const { mutateAsync: createConversation } = useCreatePatientInitiatedConversation({
    onSuccess: onConversationCreationSuccess,
  });

  return useMutation<void, IErrorPayload, ICreateConversationPayload>({
    mutationFn: async (payload: ICreateConversationPayload) => {
      createConversation(payload);
    },
    onError: () => onError(),
  });
};

export const CREATE_PATIENT_REQUESTED_PROVIDER_MESSAGE_PATH =
  '/api/v1/patient_messaging/patient_requested_provider_message';
export const usePatientRequestedProviderMessageMutation = () => {
  return useMutation<
    ICreatePatientRequestedProviderMessageResponse,
    IErrorPayload,
    ICreatePatientRequestedProviderMessagePayload
  >({
    mutationFn: async ({ title, message, tags, followUpDate = null, metadata = null }) => {
      return ApiClient.post(CREATE_PATIENT_REQUESTED_PROVIDER_MESSAGE_PATH, {
        json: {
          title,
          message,
          tags,
          follow_up_date: followUpDate,
          metadata,
        },
      });
    },
  });
};

export const GET_UNREAD_MESSAGES_PATH = '/api/v1/patient_messaging/get_unread_message_count';
interface IUseUnreadMessageCountQueryOptions {
  enabled?: boolean;
  refetchInterval?: number;
}
export const useUnreadMessageCountQuery = (options: IUseUnreadMessageCountQueryOptions) => {
  return useQuery<IUnreadMessageCountResponse, IErrorPayload>({
    queryKey: [GET_UNREAD_MESSAGES_PATH],
    ...options,
  });
};
